import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import AddressAutocomplete from '../components/ui/AddressAutocomplete';
import ImageUpload from '../components/ui/ImageUpload';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Home, 
  Plus, 
  Pencil, 
  Trash2,
  Bed,
  Bath,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Info,
  User,
  MapPin
} from 'lucide-react';
import { getFullImageUrl, getFullImageUrls } from '../lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const defaultImages = [
  'https://images.unsplash.com/photo-1760067537639-0fb475c87657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwaW50ZXJpb3IlMjBzdW5ueXxlbnwwfHx8fDE3NzE0OTgxMjJ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1710883734891-93709398496d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwaW50ZXJpb3IlMjBzdW5ueXxlbnwwfHx8fDE3NzE0OTgxMjJ8MA&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
];

// Image Gallery Component
const PropertyImageGallery = ({ images, name }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Convert all image URLs to full URLs
  const convertedImages = getFullImageUrls(images);
  const imageList = convertedImages.length > 0 ? convertedImages : [defaultImages[0]];

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % imageList.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
  };

  return (
    <div className="relative group">
      <img
        src={imageList[currentIndex]}
        alt={name}
        className="w-full h-full object-cover"
      />
      {imageList.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-black/70"
            data-testid="prev-image-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-black/70"
            data-testid="next-image-btn"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {imageList.map((_, idx) => (
              <div
                key={idx}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const PropertiesPage = () => {
  const { user, language, isAdmin } = useAuth();
  const { t } = useTranslation(language);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    property_type: 'house',
    bedrooms: 1,
    bathrooms: 1,
    images: [],
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API_URL}/properties`);
      setProperties(response.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.address || !formData.phone) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }
    
    setSaving(true);
    
    try {
      const imageUrl = formData.images.length > 0 
        ? formData.images[0] 
        : defaultImages[Math.floor(Math.random() * defaultImages.length)];
      
      const data = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        description: formData.description,
        property_type: formData.property_type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        image_url: imageUrl,
        images: formData.images,
      };
      
      if (selectedProperty) {
        await axios.put(`${API_URL}/properties/${selectedProperty.id}`, data);
        toast.success(t('propertyUpdated'));
      } else {
        await axios.post(`${API_URL}/properties`, data);
        toast.success(t('propertyCreated'));
      }
      
      setDialogOpen(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (errorMsg?.includes('already exists')) {
        toast.error(t('propertyExists'));
      } else {
        toast.error(errorMsg || t('error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (property) => {
    setSelectedProperty(property);
    setFormData({
      name: property.name,
      email: property.email || '',
      phone: property.phone || '',
      address: property.address,
      description: property.description || '',
      property_type: property.property_type,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      images: property.images || (property.image_url ? [property.image_url] : []),
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/properties/${selectedProperty.id}`);
      toast.success(t('propertyDeleted'));
      setDeleteDialogOpen(false);
      setSelectedProperty(null);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleShowInfo = async (property) => {
    try {
      const res = await axios.get(`${API_URL}/properties/${property.id}`);
      setPropertyInfo(res.data);
      setInfoDialogOpen(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleToggleStatus = async (property) => {
    const newStatus = property.status === 'dormant' ? 'active' : 'dormant';
    try {
      await axios.put(`${API_URL}/admin/properties/${property.id}/status`, { status: newStatus });
      toast.success(language === 'fr' ? `Statut changé en ${newStatus === 'active' ? 'Actif' : 'Dormant'}` : `Status changed to ${newStatus}`);
      fetchProperties();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleAdminDelete = async (property) => {
    try {
      await axios.delete(`${API_URL}/admin/properties/${property.id}`);
      toast.success(language === 'fr' ? 'Propriété supprimée' : 'Property deleted');
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const resetForm = () => {
    setSelectedProperty(null);
    setFormData({
      name: '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: '',
      description: '',
      property_type: 'house',
      bedrooms: 1,
      bathrooms: 1,
      images: [],
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      inactive: 'badge-error',
      pending: 'badge-warning',
    };
    return badges[status] || 'badge-info';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">{t('properties')}</h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' 
              ? 'Gérez vos propriétés'
              : 'Manage your properties'
            }
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={openNewDialog} data-testid="add-property-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('addProperty')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="property-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {selectedProperty ? t('editProperty') : t('createProperty')}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? 'Remplissez les informations de la propriété'
                  : 'Fill in the property information'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('propertyName')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="property-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')} *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-9"
                      required
                      data-testid="property-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')} *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-9"
                      required
                      data-testid="property-phone-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">{t('address')} *</Label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(value) => setFormData({ ...formData, address: value })}
                  placeholder={language === 'fr' ? 'Entrez l\'adresse...' : 'Enter address...'}
                  language={language}
                  data-testid="property-address-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">{t('description')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="property-description-input"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="property_type">{t('propertyType')}</Label>
                  <Select 
                    value={formData.property_type} 
                    onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                  >
                    <SelectTrigger data-testid="property-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house" data-testid="type-house">{t('house')}</SelectItem>
                      <SelectItem value="apartment" data-testid="type-apartment">{t('apartment')}</SelectItem>
                      <SelectItem value="villa" data-testid="type-villa">{t('villa')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bedrooms">{t('bedrooms')}</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    min="1"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 1 })}
                    data-testid="property-bedrooms-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bathrooms">{t('bathrooms')}</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    min="1"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: parseInt(e.target.value) || 1 })}
                    data-testid="property-bathrooms-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t('propertyPhotos')}</Label>
                <p className="text-xs text-muted-foreground mb-2">{t('allowedFormats')}</p>
                <ImageUpload
                  value={formData.images}
                  onChange={(images) => setFormData({ ...formData, images })}
                  maxFiles={5}
                  language={language}
                  data-testid="property-images-upload"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-property-btn">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-property-btn">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('save')
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Properties grid */}
      {properties.length === 0 ? (
        <Card className="card-base">
          <CardContent className="py-16 text-center">
            <Home className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">{t('noProperties')}</h3>
            <p className="text-muted-foreground mb-6">
              {language === 'fr' 
                ? 'Ajoutez votre première propriété pour commencer'
                : 'Add your first property to get started'
              }
            </p>
            <Button className="btn-primary" onClick={openNewDialog} data-testid="add-first-property-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('addProperty')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property, index) => (
            <Card 
              key={property.id} 
              className="card-base card-interactive animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`property-card-${property.id}`}
            >
              <div className="aspect-[16/10] relative overflow-hidden">
                <PropertyImageGallery 
                  images={property.images || (property.image_url ? [property.image_url] : [])} 
                  name={property.name}
                />
                <span className={`absolute top-3 right-3 ${getStatusBadge(property.status)}`}>
                  {t(property.status)}
                </span>
              </div>
              <CardContent className="p-5">
                <h3 className="font-heading font-semibold text-lg text-foreground mb-1 truncate">
                  {property.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 truncate">{property.address}</p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Bed className="h-4 w-4" />
                    <span>{property.bedrooms}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Bath className="h-4 w-4" />
                    <span>{property.bathrooms}</span>
                  </div>
                  <span className="capitalize">{t(property.property_type)}</span>
                </div>
                
                <div className="flex gap-2">
                  {isAdmin ? (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleToggleStatus(property)}
                        className={property.status === 'dormant' ? 'text-amber-600 border-amber-300' : 'text-green-600 border-green-300'}
                        data-testid={`toggle-status-${property.id}`}
                      >
                        {property.status === 'dormant' 
                          ? (language === 'fr' ? 'Dormant' : 'Dormant')
                          : (language === 'fr' ? 'Actif' : 'Active')
                        }
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleShowInfo(property)}
                        data-testid={`show-info-property-${property.id}`}
                      >
                        <Info className="h-4 w-4 mr-1" />
                        {language === 'fr' ? 'Infos' : 'Info'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => handleAdminDelete(property)}
                        data-testid={`admin-delete-property-${property.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEdit(property)}
                        data-testid={`edit-property-${property.id}`}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        {t('edit')}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setSelectedProperty(property);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-property-${property.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-property-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{t('deleteProperty')}</AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'fr' 
                ? 'Êtes-vous sûr de vouloir supprimer cette propriété ? Cette action est irréversible.'
                : 'Are you sure you want to delete this property? This action cannot be undone.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Property Info Dialog (Admin) */}
      <Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
        <DialogContent className="sm:max-w-[450px]" data-testid="property-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {propertyInfo?.name || 'Property Info'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' ? 'Informations du propriétaire' : 'Owner Information'}
            </DialogDescription>
          </DialogHeader>
          {propertyInfo && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <User className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {language === 'fr' ? 'Propriétaire' : 'Owner'}
                  </p>
                  <p className="font-semibold" data-testid="info-owner-name">{propertyInfo.owner_name || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {language === 'fr' ? 'Adresse' : 'Address'}
                  </p>
                  <p className="font-semibold" data-testid="info-address">{propertyInfo.address || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <Phone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {language === 'fr' ? 'Téléphone' : 'Phone'}
                  </p>
                  <p className="font-semibold" data-testid="info-phone">
                    <a href={`tel:${propertyInfo.owner_phone}`} className="text-primary hover:underline">
                      {propertyInfo.owner_phone || 'N/A'}
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Email</p>
                  <p className="font-semibold" data-testid="info-email">
                    <a href={`mailto:${propertyInfo.owner_email}`} className="text-primary hover:underline">
                      {propertyInfo.owner_email || 'N/A'}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoDialogOpen(false)}>
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertiesPage;
